import { inject } from "@angular/core";
import { CanActivateFn, Router } from "@angular/router";
import { catchError, map, of, switchMap } from "rxjs";
import { v4 as uuidv4 } from "uuid";
import { AuthService } from "../auth/auth.service";
import { CompanyApiService } from "../generated/waba-api/services";
import { SILENT_HTTP_CONTEXT } from "../../shared/utils/silent-http.util";
import { CompanyTrialStatusApiService } from "../company/company-trial-status-api.service";

/**
 * Sends a merchant who never finished the sign-up questionnaire back to it.
 *
 * Roles and access groups are purely selection-driven now (see
 * CompanyService.resolveDefaultRoles/resolveDefaultGroups) — an account created without
 * answering the questionnaire has NONE at all, so nothing else in the app is usable until
 * it's answered. This used to be asked right after account creation on the same page, but a
 * merchant who closed the tab or navigated away before finishing could still log back in
 * straight into a dashboard with no permissions at all — this guard catches that.
 *
 * That "no survey answered yet == no access yet" premise held when this guard first shipped
 * (2026-09-18), but stopped being true for BRAND NEW accounts the moment FULL access mode
 * became the default (2026-09-14, see CompanyService#effectiveUseCases): FULL mode grants
 * MESSAGING+CATALOG Keycloak groups at account-creation time, before the survey is ever shown.
 * The original fix here exempted "has at least one Keycloak group" to stop a pre-existing,
 * already-configured account (predating this whole feature) from being trapped in a forced
 * questionnaire — but under FULL mode that same check also, wrongly, exempts a fresh account
 * that closed the tab mid-survey and never answered it, since it already has the FULL-mode
 * baseline groups. It would then log back in straight into the dashboard with the survey never
 * asked again. (The old check was doubly fragile: a legitimately old, already-configured
 * account whose trial later expires has its groups CLEARED for read-only enforcement — see
 * CompanyService#downgradeToReadOnlyIfTrialExpired — so it would fail the old groups check too.)
 *
 * The reliable signal for "does this company's onboarding even involve a survey" is whether it
 * has a trial at all (companies.trial_started_at/trial_ends_at) — those are set ONLY by the
 * self-service sign-up flow (or an admin manually starting one), which is exactly the flow that
 * always shows the survey. A company with no trial predates self-service sign-up entirely (e.g.
 * admin-created) and was never meant to see this questionnaire. A company WITH trial dates
 * always gets checked against its real signup-survey record, regardless of current group/role
 * state — correct whether it's mid-abandoned-signup or a fully configured, later trial-expired
 * account with groups temporarily cleared.
 */
export const surveyCompletionGuard: CanActivateFn = (_route, state) => {
  const authService = inject(AuthService);
  const companyService = inject(CompanyApiService);
  const trialStatusApi = inject(CompanyTrialStatusApiService);
  const router = inject(Router);

  // Staff/admin tokens (ADMIN_SOLUTION_UI etc.) carry no companyId — this only applies to
  // merchant self-service accounts.
  if (!authService.getCompanyId()) {
    return true;
  }
  // Avoid guarding the completion page itself into a redirect loop.
  if (state.url.startsWith("/complete-profile")) {
    return true;
  }
  // Already has real access (an existing, already-configured account) — nothing to force.
  if (authService.getUserGroups().length > 0) {
    return true;
  }

  return trialStatusApi.getTrialStatus().pipe(
    switchMap((trialStatus) => {
      // No trial at all: predates self-service sign-up (e.g. admin-created) — the survey was
      // never part of this account's onboarding, so there is nothing to force.
      if (!trialStatus?.endsAt) {
        return of(true);
      }
      return companyService
        .getOwnSignupSurvey(
          { "x-api-requestId": uuidv4(), "x-api-canal": "web" },
          SILENT_HTTP_CONTEXT,
        )
        .pipe(
          map((response) => (response?.body ? true : router.parseUrl("/complete-profile"))),
        );
    }),
    // Fail open: a broken check must never lock every merchant out of the app.
    catchError(() => of(true)),
  );
};
